import { useMemo } from "react";
import { PartnerColumn, PartnerStatus } from "./types";
import { partnerStatusColors } from "./helpers";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import { getCountryDisplayName } from "@/utils/countries";
import { getCurrentLocale } from "@/utils/locale";

interface UsePartnerFilterOptionsArgs {
  countryValues: string[];
  stateRegionValues: string[];
  typeOptions: { value: string; label: string }[];
}

export const usePartnerFilterOptions = ({
  countryValues,
  stateRegionValues,
  typeOptions
}: UsePartnerFilterOptionsArgs) =>
  useMemo<DataTableFilter[]>(
    () => [
      { key: PartnerColumn.NAME, filterType: { type: "text" } },
      { key: PartnerColumn.CITY, filterType: { type: "text" } },
      { key: PartnerColumn.POSTAL_CODE, filterType: { type: "text" } },
      {
        key: PartnerColumn.STATUS,
        excludeNoValue: true,
        filterType: {
          type: "enum",
          values: {
            enumType: PartnerStatus,
            entityType: "partners",
            valueType: "statusValues",
            getIcon: (status: PartnerStatus) => (
              <IconContainer
                size="xs"
                style={{ color: partnerStatusColors[status] }}
              >
                <Bullet />
              </IconContainer>
            )
          }
        }
      },
      {
        key: PartnerColumn.TYPE,
        excludeNoValue: true,
        filterType: {
          type: "map",
          // Numeric ids, matching what the array column stores and saved views hold.
          values: typeOptions.map(({ value, label }) => ({
            value: Number(value),
            label
          }))
        }
      },
      {
        key: PartnerColumn.COUNTRY,
        filterType: {
          type: "map",
          values: countryValues.map((value: string) => ({
            value,
            label: getCountryDisplayName(value, getCurrentLocale())
          })),
          sort: true
        }
      },
      {
        key: PartnerColumn.STATE_REGION,
        filterType: {
          type: "map",
          values: stateRegionValues.map((value: string) => ({
            value,
            label: value
          })),
          sort: true
        }
      }
    ],
    [countryValues, stateRegionValues, typeOptions]
  );
